import React, { useState, useEffect } from "react";
import "./MyVouchers.css";
import { supabase } from "../supabase";
import { MYT_OFFSET_MS, mytTodayISO } from "../constants";

// Expandable "My Vouchers" section for the profile page — sibling of
// MyOrders.js, same card language. Shows the free add-on QR vouchers issued
// to this customer by admins (admin-addon-vouchers), read directly against
// add_on_vouchers under RLS ("Users can view own vouchers").
//
// RLS on the table ORs two SELECT policies together (own rows OR is_admin()),
// so an admin-tier account browsing this portal would otherwise fetch every
// customer's vouchers — including their active QR tokens. We do NOT rely on
// RLS alone here: the query is explicitly scoped with .eq("user_id", user.id).
//
// FAILS SILENT on any read error (including the table/columns not existing
// yet if the migration hasn't been applied) — this section must never break
// ProfilePage. By DEFAULT it renders NOTHING at all (no heading, no section)
// when the user has zero vouchers, so an account that has never received one
// sees no trace of the feature. Pass `emptyState` (a string or node) to opt
// into a friendly placeholder instead — used when this component is the sole
// content of its own "Vouchers" tab on the profile page, where a blank pane
// would look broken. A read error is treated the same as zero vouchers, so
// `emptyState` also covers the fail-silent case — never a crash.
//
// Above the cards, ONE "Free meal unlocked" banner shows a PENDING free-meal
// eligibility (free_meal_eligibility, RLS "own rows" — scoped with
// .eq("user_id") here too, same reasoning as above): a row with consumed_at
// NULL whose window hasn't closed means "book any court again inside the
// window and the voucher follows". The voucher itself is minted by the payment
// callback after that booking and lands in this same list once earned, so the
// banner is purely a heads-up — nothing here is redeemable. It is read
// separately from the vouchers so either query failing hides only its own
// half, and it logs nothing louder than console.debug: the table ships in its
// own migration, and a 42P01 on every profile visit until it lands is expected,
// not an error. The banner shows even when the user holds zero vouchers — an
// eligibility row means they ARE in the feature, so unlike the default
// zero-voucher case there is something to tell them.
//
// The banner quotes only the part of the window the callback will HONOUR. The
// open window is step 4 of meal_voucher.ts's state machine; step 3 — one free
// meal per Malaysia calendar week — runs before it and refuses for the rest of
// the week once any FREE-MEAL voucher exists since Monday. So the range is
// clipped to next Monday when this week's meal is already spent (decided from
// the voucher list fetched alongside), and hidden outright if nothing usable
// is left. The server is the authority here; if the two ever disagree, fix
// this copy, never the callback.

// The counter instruction, in the owner's wording (2026-09-01). Kept identical
// to COUNTER_INSTRUCTION in the admin-addon-vouchers edge function, which
// prints it in the voucher email — the customer should read one sentence, not
// two variations of it.
const COUNTER_INSTRUCTION = "Show this QR code at counter to redeem";

// Voucher terms are STATIC campaign copy — deliberately not a database column,
// so re-wording them re-words every voucher already in a customer's hands
// rather than only the ones issued from now on. Twin of TERMS_BY_CAMPAIGN in
// the admin-addon-vouchers edge function (which prints the same block in the
// email and the WhatsApp message) and of the admin portal's copy; the three
// live in different repos, so change them together.
//
// Keyed on campaign, not applied to every voucher: "not valid on drinks" must
// never appear on a voucher issued FOR a drink.
const MEAL_VOUCHER_TERMS =
  "Valid for any meal up to RM25. Not valid on drinks.\n" +
  "Kitchen hours: Tue-Sun, 10:00am-9:30pm (closed Monday).";

// The campaign tag callback-v10's meal_voucher.ts stamps on every free-meal
// voucher — and the tag its weekly cap counts. Same literal as
// FREE_MEAL_CAMPAIGN in that file, in admin-addon-vouchers and in the admin
// portal's VouchersPage.
const FREE_MEAL_CAMPAIGN = "FREE-MEAL";

const TERMS_BY_CAMPAIGN = {
  [FREE_MEAL_CAMPAIGN]: MEAL_VOUCHER_TERMS,
};

const termsForVoucher = (voucher) => TERMS_BY_CAMPAIGN[voucher?.campaign] || null;

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // Malaysia time (GMT+8) — the venue's only timezone.
  return d.toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kuala_Lumpur",
  });
};

// "8 Sep 2026, 11:59pm" — the expiry is quoted with the TIME as well as the
// date (owner, 2026-09-01): a voucher issued for 7 days dies at 11:59pm MYT on
// the 7th day, and a bare date reads as "some time that day".
const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kuala_Lumpur",
  });
};

// Calendar-day label for the eligibility window — "Tue 9 Sep". window_start /
// window_end arrive as bare "YYYY-MM-DD" strings (a Postgres `date`), and they
// are MALAYSIA calendar dates. They deliberately do NOT go through formatDate
// above: `new Date("2026-09-09")` is UTC midnight, which a browser west of
// Greenwich renders as the 8th, and the Asia/Kuala_Lumpur formatter cannot
// undo a day that was already lost at parse time. The label is built from the
// parts instead; Date.UTC + getUTCDay only supplies the weekday, and is immune
// to the browser's zone.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const formatCalendarDay = (isoDate) => {
  if (!ISO_DATE_RE.test(isoDate || "")) return "";
  const [y, m, d] = isoDate.split("-").map(Number);
  const weekday = DAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${weekday} ${d} ${MONTH_NAMES[m - 1]}`;
};

// Calendar arithmetic on a "YYYY-MM-DD" string via UTC midnight (Date.UTC
// normalises day overflow), so month and year ends roll correctly and the
// browser's zone never leaks in. Twin of addDaysToDateString in
// callback-v10/meal_voucher.ts.
const addDaysISO = (isoDate, days) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

// Monday of the Malaysia week containing `todayISO`, as "YYYY-MM-DD". "One
// free meal per week" is a CALENDAR-week rule starting Monday (mytWeekStartUtc
// in meal_voucher.ts, the convention free_slots use), not a rolling 7 days.
const weekMondayISO = (todayISO) => {
  const [y, m, d] = todayISO.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDaysISO(todayISO, -(dow === 0 ? 6 : dow - 1));
};

// Has this customer already been HANDED a free meal this Malaysia week? Mirrors
// the callback's weekly cap (meal_voucher.ts step 3) exactly: any FREE-MEAL
// voucher created since Monday 00:00 MYT counts — voided or redeemed ones too,
// the rule is about meals handed out — and the server checks it BEFORE the
// open window, so a booking on any day left in that week earns nothing however
// wide the window is. Decided from the voucher list this component already
// holds (newest-first, so a same-week voucher can never fall past the fetch
// limit) rather than a third query. In the normal flow the cap and an open
// window cannot coexist (the cap also blocks GRANTING); they meet only when an
// admin hand-issues a "Free meal" comp onto an open window, or when a voucher
// committed after its issue call timed out and the callback un-claimed the
// window. In both, the window is real but the next usable day is Monday.
const mealCappedThisWeek = (vouchers, todayISO) => {
  const weekStartMs = Date.parse(`${weekMondayISO(todayISO)}T00:00:00Z`) - MYT_OFFSET_MS;
  return vouchers.some(
    (v) =>
      v?.campaign === FREE_MEAL_CAMPAIGN &&
      !!v.created_at &&
      new Date(v.created_at).getTime() >= weekStartMs
  );
};

// Banner body. "YYYY-MM-DD" strings order correctly as plain strings, so every
// comparison stays on Malaysia calendar dates with no Date parsing at all.
//
// The range quoted is the part of the window the SERVER will honour: with this
// week's meal already spent (`capped`) the start moves to next Monday, and a
// window that closes before then is not shown at all — every day left in it
// is one the callback refuses. Since 2026-09-24 a window is open the moment it
// is granted, so the usual case quotes only the DEADLINE ("by <date>") — a
// range starting today just invited the reader to wait, which is what the
// first complaint was about. The "from tomorrow" branch survives for rows
// granted under the old rule, and "between … and …" for a clipped range.
const mealEligibilityCopy = ({ window_start, window_end }, { todayISO, capped }) => {
  if (!ISO_DATE_RE.test(window_start) || !ISO_DATE_RE.test(window_end)) return null;
  const nextMonday = addDaysISO(weekMondayISO(todayISO), 7);
  const clipped = capped && nextMonday > window_start;
  const effectiveStart = clipped ? nextMonday : window_start;
  if (effectiveStart > window_end) return null;

  const start = formatCalendarDay(effectiveStart);
  const end = formatCalendarDay(window_end);
  const isTomorrow = effectiveStart === addDaysISO(todayISO, 1);
  const startsNow = effectiveStart <= todayISO;
  let when;
  if (effectiveStart === window_end) {
    // Only reachable when clipping leaves the window's last day — a "between
    // Mon and Mon" would read as a typo.
    when = (
      <>
        on <strong>{end}</strong>
        {isTomorrow && " (tomorrow)"}
      </>
    );
  } else if (startsNow) {
    // The normal case now: the window is already open, so the only date that
    // matters is the last day it can be used.
    when = (
      <>
        by <strong>{end}</strong>
      </>
    );
  } else if (isTomorrow) {
    when = (
      <>
        from tomorrow (<strong>{start}</strong>) until <strong>{end}</strong>
      </>
    );
  } else {
    when = (
      <>
        between <strong>{start}</strong> and <strong>{end}</strong>
      </>
    );
  }
  return (
    <>
      {clipped &&
        "You've already had this week's free meal, so your next one starts from Monday. "}
      Book any court again {when} and we&apos;ll send you a free meal voucher. One
      free meal per week.
    </>
  );
};

// Status is derived client-side purely for display, mirroring the same
// precedence as the voucher_detail RPC: voided > redeemed > expired > active.
const voucherStatus = (voucher) => {
  if (voucher.voided_at) {
    return { key: "voided", label: "Voided", detail: "No longer valid" };
  }
  if (voucher.redeemed_at) {
    return { key: "redeemed", label: "Redeemed", detail: formatDate(voucher.redeemed_at) };
  }
  const expired =
    voucher.expires_at && new Date(voucher.expires_at).getTime() <= Date.now();
  if (expired) {
    return { key: "expired", label: "Expired", detail: formatDate(voucher.expires_at) };
  }
  return { key: "active", label: "Active", detail: "Ready to redeem" };
};

const qrUrlForToken = (token) => {
  if (!token) return null;
  const { data } = supabase.storage.from("voucher-qr").getPublicUrl(`${token}.png`);
  return data?.publicUrl || null;
};

const MyVouchers = ({ emptyState = null }) => {
  const [vouchers, setVouchers] = useState([]);
  // True once the voucher read has settled either way. The banner's range
  // depends on the list (weekly cap), so it waits for this rather than
  // flashing an un-clipped promise for the moment the list takes to arrive.
  const [vouchersReady, setVouchersReady] = useState(false);
  // The storage URL is a pure string builder — it resolves even when the
  // backend's (best-effort) QR mint never uploaded the PNG. Track per-voucher
  // load failures so we can fall back to the token instead of a broken image.
  const [qrErrors, setQrErrors] = useState({});
  // The one pending free-meal window ({ window_start, window_end }), or null.
  const [mealEligibility, setMealEligibility] = useState(null);

  useEffect(() => {
    let isActive = true;

    const fetchVouchers = async (user) => {
      try {
        const { data, error } = await supabase
          .from("add_on_vouchers")
          .select(
            "id, token, quantity, campaign, created_at, expires_at, redeemed_at, voided_at, add_ons (name)"
          )
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(100);

        if (error) throw error;

        if (isActive) setVouchers(data || []);
      } catch (err) {
        // Fail silent: never break ProfilePage over this section (e.g. the
        // migration not applied yet would 42703 the query).
        console.error("MyVouchers.js: Error fetching vouchers:", err);
        if (isActive) setVouchers([]);
      } finally {
        if (isActive) setVouchersReady(true);
      }
    };

    const fetchMealEligibility = async (user) => {
      try {
        // Newest-closing pending window wins if there is ever more than one.
        // "today" is the Malaysia calendar date, NOT the browser's — a window
        // that closes today must still show to a customer whose phone is set
        // to a zone where it is already tomorrow.
        const { data, error } = await supabase
          .from("free_meal_eligibility")
          .select("window_start, window_end")
          .eq("user_id", user.id)
          .is("consumed_at", null)
          .gte("window_end", mytTodayISO())
          .order("window_end", { ascending: false })
          .limit(1);

        if (error) throw error;

        const row = Array.isArray(data) ? data[0] : null;
        const usable =
          !!row && ISO_DATE_RE.test(row.window_start) && ISO_DATE_RE.test(row.window_end);
        if (row && !usable) {
          // A row came back but its dates are not the bare "YYYY-MM-DD" a
          // Postgres `date` serialises to (the migration declares both columns
          // `date`). Deliberately NOT normalised with slice(0, 10): were a
          // column ever to become a timestamptz, PostgREST would serialise a
          // Malaysia midnight as the PREVIOUS day at 16:00Z and the slice
          // would quote the wrong day. Hiding is the safe outcome — but it
          // must not be a silent one, or every eligible customer loses the
          // banner with nothing in the console to say why.
          console.debug(
            "MyVouchers.js: free-meal eligibility row has unexpected date shape:",
            row
          );
        }
        if (isActive) setMealEligibility(usable ? row : null);
      } catch (err) {
        // Fail silent AND quiet (console.debug, not error): until the
        // free_meal_eligibility migration is applied this 42P01s on every
        // profile visit, which is expected rather than a fault.
        console.debug("MyVouchers.js: free-meal eligibility unavailable:", err);
        if (isActive) setMealEligibility(null);
      }
    };

    const load = async () => {
      let user = null;
      try {
        const { data } = await supabase.auth.getUser();
        user = data?.user || null;
      } catch (err) {
        console.error("MyVouchers.js: Error resolving user:", err);
      }
      if (!user) {
        if (isActive) {
          setVouchers([]);
          setMealEligibility(null);
        }
        return;
      }
      // Independent reads: neither one's failure hides the other.
      await Promise.all([fetchVouchers(user), fetchMealEligibility(user)]);
    };

    load();
    return () => {
      isActive = false;
    };
  }, []);

  const todayISO = mytTodayISO();
  const mealCopy =
    mealEligibility && vouchersReady
      ? mealEligibilityCopy(mealEligibility, {
          todayISO,
          capped: mealCappedThisWeek(vouchers, todayISO),
        })
      : null;
  const mealBanner = mealCopy && (
    <div className="voucher-eligibility-banner" role="status">
      <div className="voucher-eligibility-title">Free meal unlocked</div>
      <div className="voucher-eligibility-body">{mealCopy}</div>
    </div>
  );

  if (vouchers.length === 0) {
    if (!emptyState && !mealBanner) return null;
    return (
      <>
        <div className="step-title" style={{ marginTop: "24px" }}>My Vouchers</div>
        {mealBanner}
        {emptyState && <div className="my-vouchers-empty">{emptyState}</div>}
      </>
    );
  }

  return (
    <>
      <div className="step-title" style={{ marginTop: "24px" }}>My Vouchers</div>
      {mealBanner}
      <div className="my-vouchers-list">
        {vouchers.map((voucher) => {
          const status = voucherStatus(voucher);
          const addOnName = voucher.add_ons?.name || "Add-on";
          const qrUrl = status.key === "active" ? qrUrlForToken(voucher.token) : null;
          const qrFailed = !!qrErrors[voucher.id];

          return (
            <div key={voucher.id} className={`voucher-card voucher-${status.key}`}>
              <div className="voucher-card-info">
                <div className="voucher-card-title">
                  <span className="voucher-card-name">{addOnName}</span>
                  <span className="voucher-card-qty">× {voucher.quantity}</span>
                </div>
                <div className="voucher-card-status-row">
                  <span className={`voucher-status-chip ${status.key}`}>{status.label}</span>
                  {status.detail && <span className="voucher-card-detail">{status.detail}</span>}
                </div>
                {voucher.expires_at && status.key === "active" && (
                  <div className="voucher-card-expiry">
                    Valid until {formatDateTime(voucher.expires_at)} (Malaysia time)
                  </div>
                )}
                {termsForVoucher(voucher) && (
                  <div className="voucher-card-terms">{termsForVoucher(voucher)}</div>
                )}
              </div>

              {status.key === "active" && qrUrl && (
                <div className="voucher-card-qr">
                  {!qrFailed && (
                    <img
                      src={qrUrl}
                      alt={`Voucher QR for ${addOnName}`}
                      className="voucher-qr-image"
                      onError={() =>
                        setQrErrors((prev) => ({ ...prev, [voucher.id]: true }))
                      }
                    />
                  )}
                  <span className={`voucher-qr-token ${qrFailed ? "voucher-qr-token-fallback" : ""}`}>
                    {voucher.token}
                  </span>
                  {!qrFailed && <span className="voucher-qr-instruction">{COUNTER_INSTRUCTION}</span>}
                  {qrFailed && (
                    <span className="voucher-qr-error-hint">
                      QR unavailable — quote this code at the counter.
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MyVouchers;
